// Public reader for SEO + code-injection settings (world-readable per rules).
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";

// pathname → scope key (mirrors admin SEO_PAGES). Unmatched routes use global.
const PATH_SCOPE = {
  "/": "home",
  "/why-me/about-me": "about",
  "/why-me/services": "services",
  "/framework": "framework",
  "/listings": "listings",
  "/success-stories/proven-results": "proven-results",
  "/success-stories/testimonials": "testimonials",
  "/success-stories/awards": "awards",
  "/resources/calculators": "calculators",
  "/resources/property-valuation": "property-valuation",
  "/resources/blogs": "blogs",
  "/contact": "contact",
};

export function scopeForPath(pathname) {
  return PATH_SCOPE[pathname] || null;
}

const getDocSafe = async (coll, id) => {
  try {
    const s = await getDoc(doc(db, coll, id));
    return s.exists() ? s.data() : null;
  } catch {
    return null;
  }
};

/** Merge global + page-scoped doc (page overrides). */
async function merged(coll, scopeKey) {
  const [g, p] = await Promise.all([
    getDocSafe(coll, "global"),
    scopeKey ? getDocSafe(coll, scopeKey) : null,
  ]);
  return { ...(g || {}), ...(p || {}) };
}

// Blog detail routes (/resources/blogs/:slug) aren't in PATH_SCOPE — instead we
// merge the blogs-scope + global SEO as a base, then override with the post's
// own meta (metaTitle/metaDesc/keywords/image), mirroring MeProperty.
const BLOG_DETAIL = /^\/resources\/blogs\/([^/]+)$/;

export async function getSeoFor(pathname) {
  const m = pathname.match(BLOG_DETAIL);
  if (m) {
    const slug = decodeURIComponent(m[1]);
    const [base, post] = await Promise.all([
      merged("seo", "blogs"),
      getDocSafe("blogs", slug),
    ]);
    if (!post || post.status !== "published") return base;
    return {
      ...base,
      title: post.metaTitle || post.title || base.title,
      description: post.metaDesc || post.excerpt || base.description,
      keywords: Array.isArray(post.keywords)
        ? post.keywords.join(", ")
        : post.keywords || base.keywords,
      ogType: "article",
      ogImage: post.image || base.ogImage,
    };
  }
  return merged("seo", scopeForPath(pathname));
}
export const getCodeFor = (pathname) => merged("codeInjection", scopeForPath(pathname));
export const getSettings = () => getDocSafe("settings", "site");

/** Build JSON-LD blocks from SEO toggles + site settings. */
export function buildJsonLd(seo, settings, url) {
  const sd = seo?.structuredData || {};
  const blocks = [];
  const name = settings?.brandName || "A Homely Abode";
  const owner = settings?.owner || "Kiap Yong";
  if (sd.organization) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Organization",
      name,
      url,
      sameAs: Object.values(settings?.social || {}),
    });
  }
  if (sd.person) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "Person",
      name: owner,
      jobTitle: "Real Estate Consultant",
      worksFor: name,
      url,
    });
  }
  if (sd.localBusiness) {
    blocks.push({
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name,
      areaServed: "Singapore",
      url,
      telephone: settings?.whatsapp ? `+${settings.whatsapp}` : undefined,
    });
  }
  return blocks;
}
